/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useMemo, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '../../../../_fds/assets/ts/_utils'
import {useThemeMode} from '../../../../_fds/partials/layout/theme-mode/ThemeModeProvider'
import {ChartDataModel} from '../core/_models'
import {getChartData} from '../core/_requests'
import {formatMeasureValue, stripSuffix} from '../core/_formats'
import {useStore} from '../store'

type Props = {
  className: string
}

type BarSeries = {name: string; data: number[]; format?: string}

const SLUG = 'projected_vs_target_labor'
// Shown until the config's own name arrives with the data.
const FALLBACK_TITLE = 'Projected vs Target Labor'

const WeeklySummaryChart: React.FC<Props> = ({className}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {mode} = useThemeMode()
  const {selectedLocationId, selectedDate} = useStore()
  // The stored config is a pure STWv3 config; the endpoint returns the df as
  // { columns, rows }. Each measure column becomes a bar series.
  const [chartData, setChartData] = useState<ChartDataModel | undefined>(undefined)

  useEffect(() => {
    let active = true
    if (selectedLocationId) {
      getChartData(SLUG, selectedLocationId, selectedDate).then((data) => {
        if (active) setChartData(data)
      })
    }
    return () => {
      active = false
    }
  }, [selectedLocationId, selectedDate])

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, chartData])

  const refreshChart = () => {
    // Don't render before the data arrives — apexcharts 3.54 throws inside
    // render() on an empty bar series ("Cannot read properties of undefined
    // (reading 'length')").
    if (!chartRef.current || !chartData?.columns?.length || !chartData?.rows?.length) {
      return
    }
    const height = parseInt(getCSS(chartRef.current, 'height'))
    const chart = new ApexCharts(chartRef.current, getChartOptions(height, chartData))
    if (chart) {
      chart.render()
    }
    return chart
  }

  const renderChat = () => {
    return (
      <div className={`card ${className}`}>
        {/* begin::Header */}
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            {/* The card titles itself from the chart config (name/description),
                so renaming the config renames the widget. The fallbacks only
                show before the first response arrives. */}
            <span className='card-label fw-semibold fs-3 mb-1'>
              {chartData?.name || FALLBACK_TITLE}
            </span>
            <span className='text-muted fw-semibold fs-7'>{chartData?.description || ''}</span>
          </h3>
        </div>
        {/* end::Header */}
        {/* begin::Body */}
        <div className='card-body'>
          <div ref={chartRef} id='weekly-summary-chart' style={{height: '350px'}} />
        </div>
        {/* end::Body */}
      </div>
    )
  }

  const chart = useMemo(() => {
    return renderChat()
  }, [chartData])
  return chart
}

export {WeeklySummaryChart}

function getChartOptions(height: number, chartData?: ChartDataModel): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-primary')
  const secondaryColor = getCSSVariableValue('--bs-gray-300')

  const columns = chartData?.columns ?? []
  const rows = chartData?.rows ?? []
  const formats = chartData?.formats ?? {}
  const dimCol = columns[0]
  const measureCols = columns.slice(1)

  // Values stay raw (a percent measure is a fraction here); the config's format
  // code turns them into display strings on the axis and in the tooltip, the
  // same way TableSummary formats its cells.
  const barSeries: BarSeries[] = measureCols.map((col) => ({
    name: stripSuffix(col),
    format: formats[col],
    data: rows.map((r) => Number(r[col]) || 0),
  }))
  const categories = rows.map((r) => (dimCol ? String(r[dimCol]) : ''))

  // One shared axis, so it can only carry one format code — the measures here
  // (projected vs target labor) share one. If a config ever mixes them, the
  // axis falls back to plain numbers while the tooltip stays per-series.
  const axisFormat = barSeries.every((s) => s.format === barSeries[0]?.format)
    ? barSeries[0]?.format
    : undefined

  return {
    series: barSeries.map(({name, data}) => ({name, data})),
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height: height,
      toolbar: {show: false},
      // Recreated whenever the weekly summary or labor costs update during
      // page load — skip the grow-in animation so the chart doesn't lag the
      // rest of the overview.
      animations: {enabled: false},
    },
    plotOptions: {
      bar: {horizontal: false, columnWidth: '30%', borderRadius: 5},
    },
    legend: {show: false},
    dataLabels: {enabled: false},
    stroke: {show: true, width: 2, colors: ['transparent']},
    xaxis: {
      categories: categories,
      axisBorder: {show: false},
      axisTicks: {show: false},
      labels: {style: {colors: labelColor, fontSize: '12px'}},
    },
    yaxis: {
      labels: {
        style: {colors: labelColor, fontSize: '12px'},
        formatter: function (val) {
          return formatMeasureValue(val, axisFormat)
        },
      },
    },
    fill: {opacity: 1},
    states: {
      normal: {filter: {type: 'none', value: 0}},
      hover: {filter: {type: 'none', value: 0}},
      active: {allowMultipleDataPointsSelection: false, filter: {type: 'none', value: 0}},
    },
    tooltip: {
      style: {fontSize: '12px'},
      y: {
        formatter: function (val, opts) {
          return formatMeasureValue(val, barSeries[opts?.seriesIndex]?.format)
        },
      },
    },
    colors: [baseColor, secondaryColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {lines: {show: true}},
    },
  }
}
